import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { useNotificationStore } from '../../features/notifications/store/useNotificationStore.js';

export const AnimatedToast: React.FC = () => {
  const { toast, clearToast } = useNotificationStore();

  return (
    <div className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 z-50 sm:max-w-sm pointer-events-none">
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.9, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 20, scale: 0.92, filter: 'blur(4px)' }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            className="pointer-events-auto bg-white/95 backdrop-blur-md border border-slate-200/90 shadow-2xl rounded-2xl p-4 flex items-start gap-3 relative overflow-hidden"
          >
            {/* Status indicator bar on left edge */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1.5 ${
                toast.type === 'success'
                  ? 'bg-emerald-500'
                  : toast.type === 'error'
                  ? 'bg-rose-500'
                  : 'bg-indigo-500'
              }`}
            />

            {/* Icon */}
            <div className="shrink-0 pt-0.5">
              {toast.type === 'success' ? (
                <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-200/60">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
              ) : toast.type === 'error' ? (
                <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center border border-rose-200/60">
                  <AlertCircle className="w-5 h-5" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-200/60">
                  <Info className="w-5 h-5" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-2">
              <h4 className="text-xs font-bold text-slate-900 leading-tight truncate">
                {toast.title}
              </h4>
              <p className="text-[11px] text-slate-600 mt-0.5 leading-snug break-words">
                {toast.message}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={clearToast}
              className="shrink-0 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Animated progress bar at bottom */}
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: 4.5, ease: 'linear' }}
              style={{ originX: 0 }}
              className={`absolute bottom-0 left-0 right-0 h-0.5 ${
                toast.type === 'success'
                  ? 'bg-emerald-500/40'
                  : toast.type === 'error'
                  ? 'bg-rose-500/40'
                  : 'bg-indigo-500/40'
              }`}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
